import React from "react";

function BookBlueprintMark() {
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 14.5c7.7-1.2 14.2.4 20 4.9v32.1c-5.8-4.5-12.3-6.1-20-4.9V14.5Z"
        fill="currentColor"
        opacity="0.18"
      />
      <path
        d="M52 14.5c-7.7-1.2-14.2.4-20 4.9v32.1c5.8-4.5 12.3-6.1 20-4.9V14.5Z"
        fill="currentColor"
        opacity="0.30"
      />
      <path
        d="M12 14.5c7.7-1.2 14.2.4 20 4.9 5.8-4.5 12.3-6.1 20-4.9v32.1c-7.7-1.2-14.2.4-20 4.9-5.8-4.5-12.3-6.1-20-4.9V14.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M32 19.4v32.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function statusStyle(
  status,
  active
) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "3px 7px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  };

  if (active) {
    return {
      ...base,
      background:
        "rgba(255,255,255,0.22)",
      color: "#ffffff",
    };
  }

  if (
    status === "ready" ||
    status === "published"
  ) {
    return {
      ...base,
      background: "#dcfce7",
      color: "#166534",
    };
  }

  if (
    status === "in_progress" ||
    status === "needs_attention"
  ) {
    return {
      ...base,
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  return {
    ...base,
    background: "#e2e8f0",
    color: "#475569",
  };
}

/*
 * Shared NextScenes Book Blueprint presentation grammar.
 * Categories supply their own workspace labels, status logic and accent colour.
 */
export default function NextScenesBookBlueprintNavigator({
  currentWorkspace = "overview",
  projectTitle = "",
  untitledTitle = "Untitled Book",
  workspaces = [],
  readyCount = 0,
  statusTotal = 0,
  onNavigate,
  isFR = false,
  accent = "#2563eb",
  accentDark = "#1d4ed8",
  accentSoft = "#eff6ff",
  border = "rgba(37,99,235,0.24)",
  background =
    "linear-gradient(180deg, rgba(239,246,255,0.98) 0%, #ffffff 100%)",
  blueprintLabel =
    "NextScenes Book Blueprint",
}) {
  const title =
    String(
      projectTitle || ""
    ).trim() ||
    untitledTitle;

  return (
    <nav
      aria-label={
        isFR
          ? "Navigation du NextScenes Book Blueprint"
          : "NextScenes Book Blueprint navigation"
      }
      className="ns-card ns-nbb-section-shell ns-nbb-navigator"
      style={{
        padding: 16,
        marginBottom: 20,
        borderRadius: 18,
        border:
          `1px solid ${border}`,
        background,
      }}
    >
      <div className="ns-nbb-identity-row">
        <div className="ns-nbb-identity-lockup">
          <div
            className="ns-nbb-identity-mark"
            aria-hidden="true"
            style={{
              color: accent,
            }}
          >
            <BookBlueprintMark />
          </div>

          <div className="ns-nbb-identity-copy">
            <div
              className="ns-nbb-eyebrow"
              style={{
                color: accentDark,
              }}
            >
              {blueprintLabel}
            </div>

            <div className="ns-nbb-navigator-title">
              {title}
            </div>
          </div>
        </div>

        <div className="ns-nbb-status-block">
          <span
            className="ns-nbb-status-badge"
            style={{
              borderColor: border,
              color: accentDark,
              background:
                accentSoft,
            }}
          >
            {isFR
              ? `${readyCount} sur ${statusTotal} prêts`
              : `${readyCount} of ${statusTotal} ready`}
          </span>

          <span className="ns-nbb-navigator-guidance">
            {isFR
              ? "Choisissez où travailler"
              : "Choose where to work"}
          </span>
        </div>
      </div>

      <ul
        className="ns-nbb-workspace-grid"
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(155px, 1fr))",
          gap: 8,
          padding: 0,
          margin: 0,
          listStyle: "none",
        }}
      >
        {workspaces.map(
          (workspace) => {
            const active =
              currentWorkspace ===
              workspace.id;

            const disabled =
              workspace.disabled ===
              true;

            return (
              <li key={workspace.id}>
                <button
                  className={
                    active
                      ? "ns-nbb-workspace-button ns-nbb-workspace-button-active"
                      : "ns-nbb-workspace-button"
                  }
                  type="button"
                  disabled={disabled}
                  aria-current={
                    active
                      ? "page"
                      : undefined
                  }
                  onClick={() => {
                    if (
                      disabled ||
                      typeof onNavigate !==
                        "function"
                    ) {
                      return;
                    }

                    onNavigate(
                      workspace.id
                    );
                  }}
                  style={{
                    width: "100%",
                    minHeight: 64,
                    padding:
                      "10px 11px",
                    borderRadius: 12,
                    border: active
                      ? `1px solid ${accentDark}`
                      : "1px solid #dbe2ea",
                    background: active
                      ? accentDark
                      : "#ffffff",
                    color: active
                      ? "#ffffff"
                      : "#1e293b",
                    cursor: disabled
                      ? "not-allowed"
                      : "pointer",
                    opacity: disabled
                      ? 0.62
                      : 1,
                    display: "flex",
                    flexDirection:
                      "column",
                    alignItems:
                      "flex-start",
                    justifyContent:
                      "space-between",
                    gap: 7,
                    textAlign: "left",
                    font: "inherit",
                  }}
                >
                  <span
                    className="ns-nbb-workspace-label"
                    style={{
                      fontWeight: 900,
                      lineHeight: 1.25,
                    }}
                  >
                    {workspace.label}
                  </span>

                  <span
                    style={statusStyle(
                      workspace.status,
                      active
                    )}
                  >
                    {
                      workspace.statusLabel
                    }
                  </span>

                  {workspace.diagnostic ? (
                    <span
                      className="ns-nbb-workspace-diagnostic"
                      style={{
                        fontSize: 11,
                        lineHeight: 1.4,
                        fontWeight: 600,
                        color: active
                          ? "rgba(255,255,255,0.94)"
                          : accentDark,
                      }}
                    >
                      {
                        workspace.diagnostic
                      }
                    </span>
                  ) : null}
                </button>
              </li>
            );
          }
        )}
      </ul>
    </nav>
  );
}
